import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { useModals, scrollToSection } from "@/lib/modal";
import { getMe } from "@/lib/api";

const LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Verifiers", href: "#validators" },
  { label: "Enterprise", href: "#enterprise" },
  { label: "Roadmap", href: "#roadmap" },
];

const LogoMark = () => (
  <img
    src="/logo-lockup.png"
    alt="Synaphy - Intelligence for the physical world"
    className="h-16 w-auto object-contain drop-shadow-[0_0_18px_rgba(90,150,255,0.35)] transition-transform duration-300 group-hover:scale-105"
  />
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [memberName, setMemberName] = useState(null);
  const { openJoin, openValidator } = useModals();

  useEffect(() => {
    const check = () => {
      getMe()
        .then((m) => setMemberName(m.name.split(" ")[0]))
        .catch(() => setMemberName(null));
    };
    check();
    window.addEventListener("pageshow", check);
    window.addEventListener("focus", check);
    return () => {
      window.removeEventListener("pageshow", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else if (!document.querySelector('[data-state="open"][role="dialog"]')) window.__lenis?.start();
  }, [open]);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToSection(href), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-ink-950/70 backdrop-blur-xl border-b border-white/5" : "bg-transparent border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
            className="flex items-center gap-3 group"
            aria-label="Synaphy home"
          >
            <LogoMark />
          </button>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => go(l.href)}
                className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/portal"
              data-testid="nav-link-portal"
              className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125"
            >
              Portal
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-6">
            <Link
              to="/portal"
              data-testid="nav-login-btn"
              className="text-sm font-medium text-silver-glass transition-[filter] duration-300 hover:brightness-125"
            >
              {memberName ? memberName : "Login"}
            </Link>
            <button
              data-testid="nav-join-btn"
              onClick={openJoin}
              className="group inline-flex items-center gap-2 rounded-lg bg-signal-cyan px-5 py-2.5 text-sm font-semibold text-ink-950 transition-[background-color,box-shadow] duration-300 hover:bg-cyan-300 hover:shadow-[0_8px_32px_rgba(34,211,238,0.25)]"
            >
              Join the Network
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="lg:hidden text-mist p-2"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-ink-950/[0.98] backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-[72px] px-6 flex items-center justify-between">
              <img src="/logo-lockup.png" alt="Synaphy" className="h-11 w-auto object-contain" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-mist p-2"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div
              className="flex-1 flex flex-col justify-start px-8 pt-10 gap-1 overflow-y-auto overscroll-contain"
              data-lenis-prevent
            >
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-3xl sm:text-4xl font-bold tracking-tight text-mist/90 py-3 border-b border-white/5 transition-colors hover:text-signal-cyan"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to="/portal"
                  data-testid="mobile-link-portal"
                  onClick={() => setOpen(false)}
                  className="block text-left font-display text-3xl sm:text-4xl font-bold tracking-tight text-signal-cyan py-3 border-b border-white/5"
                >
                  Portal
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="mt-8 flex flex-col gap-3 pb-10"
              >
                <button
                  data-testid="mobile-join-btn"
                  onClick={() => { setOpen(false); openJoin(); }}
                  className="rounded-lg bg-signal-cyan px-7 py-4 text-sm font-semibold text-ink-950"
                >
                  Join the Founding Network
                </button>
                <button
                  data-testid="mobile-validator-btn"
                  onClick={() => { setOpen(false); openValidator(); }}
                  className="rounded-lg border border-white/20 px-7 py-4 text-sm font-semibold text-mist"
                >
                  Become a Founding Verifier
                </button>
                <Link
                  to="/portal"
                  data-testid="mobile-login-btn"
                  onClick={() => setOpen(false)}
                  className="py-3 text-center text-sm font-medium text-steel"
                >
                  {memberName ? `${memberName} — open your Portal` : "Login to Portal"}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
